import React, { useEffect, useState } from 'react'
import { Button, Grid } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { attendeeFormSchema } from 'schemas/attendee'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import ProgressButton from 'components/ProgressButton'
import CustomTextField from 'components/Form/CustomTextField'
import CustomBox from 'components/Form/CustomBox'
import CustomSelect from 'components/Form/CustomSelect'
import { fetchAttendeeById, updateAttendee } from 'store/attendee'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { AttendeeRoles } from 'constants/index'
import { toast } from 'react-toastify'
import {
  formatPhoneNumberIntl,
  getCountryCallingCode,
  parsePhoneNumber
} from 'react-phone-number-input'
import PhoneNumberInput from 'components/PhoneNumberInput'

const AttendeeForm = () => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { attendee, inProgress } = useSelector((s) => s.attendee)
  const history = useHistory()
  const { attendeeId } = useParams()

  const parsePhoneNumberToIntl = (phoneNumber, countryCode) => {
    if (phoneNumber) {
      const code = getCountryCallingCode(countryCode)
      const parsedPhone = formatPhoneNumberIntl(`+${code}${phoneNumber}`)
      return parsedPhone
    }
    return undefined
  }

  const initialValues = {
    firstName: attendee?.firstName || '',
    lastName: attendee?.lastName || '',
    position: attendee?.position || '',
    email: attendee?.email || '',
    phoneNumber: attendee?.phoneNumber
      ? parsePhoneNumberToIntl(attendee?.phoneNumber, attendee?.countryCode)
      : '',
    company: attendee?.company || '',
    role: attendee?.role || AttendeeRoles.ATTENDEE,
    countryCode: attendee?.countryCode || 'US'
  }

  const goBack = () => {
    history.goBack()
  }

  const onSubmit = async (values) => {
    setLoading(true)
    const payload = {
      firstName: values.firstName?.trim(),
      lastName: values.lastName?.trim(),
      position: values.position?.trim(),
      email: values.email?.trim(),
      phoneNumber: values?.phoneNumber?.trim(),
      company: values.company?.trim(),
      countryCode: values.countryCode?.trim(),
      role: values.role
    }
    if (payload.phoneNumber) {
      const parsedPhone = parsePhoneNumber(payload.phoneNumber)
      if (parsedPhone?.nationalNumber) {
        payload.phoneNumber = parsedPhone?.nationalNumber
        payload.countryCode = parsedPhone?.country
      }
    }
    dispatch(
      updateAttendee(
        attendeeId,
        payload,
        () => {
          setLoading(false)
          toast.success('Attendee has been updated successfully')
          goBack()
        },
        () => {
          setLoading(false)
          toast.error('Failed to update the attendee')
        }
      )
    )
  }

  useEffect(() => {
    dispatch(fetchAttendeeById(attendeeId))
  }, [])

  return (
    <CustomBox>
      <ViewHeader title="Edit Attendee">
        <div style={{ marginRight: 10 }}>
          <ActionBar actionText="Back" onAction={goBack} />
        </div>
      </ViewHeader>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={attendeeFormSchema}
        onSubmit={onSubmit}>
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomTextField label="First Name" name="firstName" type="text" />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField label="Last Name" name="lastName" type="text" />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField label="Position" name="position" type="text" />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField label="Email" name="email" disabled type="text" />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneNumberInput
                  international
                  countryCallingCodeEditable={false}
                  helperText={touched?.phoneNumber && errors?.phoneNumber}
                  error={Boolean(touched?.phoneNumber && errors?.phoneNumber)}
                  placeholder="Phone Number"
                  label="Phone Number"
                  value={values.phoneNumber}
                  onChange={(val) => setFieldValue('phoneNumber', val)}
                  onCountryChange={(code) => setFieldValue('countryCode', code)}
                  country={values.countryCode}
                  defaultCountry={values.countryCode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField label="Institution" name="company" type="text" />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  inputLabel="Role"
                  options={Object.values(AttendeeRoles).map((item) => ({
                    id: item,
                    role: item
                  }))}
                  optionAttr={'role'}
                  name="role"
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button size="large" disabled={inProgress} onClick={() => goBack()}>
                  Cancel
                </Button>
              </Grid>

              <Grid item>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={inProgress || loading}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </CustomBox>
  )
}
export default AttendeeForm
